import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Send, Sparkles, Phone } from 'lucide-react';

// Apple-style triple dot loading indicator
const TypingIndicator = ({ size = 'default' }: { size?: 'default' | 'small' }) => {
  const dotSize = size === 'small' ? 'h-1.5 w-1.5' : 'h-2 w-2';
  return (
    <div className="flex gap-1 items-center">
      <div className={`${dotSize} bg-current rounded-full animate-[bounce_1.4s_ease-in-out_infinite]`} />
      <div className={`${dotSize} bg-current rounded-full animate-[bounce_1.4s_ease-in-out_0.2s_infinite]`} />
      <div className={`${dotSize} bg-current rounded-full animate-[bounce_1.4s_ease-in-out_0.4s_infinite]`} />
    </div>
  );
};
import { useChatStream, type Message } from '@/hooks/useChatStream';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import {
  getOrCreateConversation,
  createNewConversation,
  getConversationMessages,
  saveMessage,
  updateConversationTitle,
} from '@/data/askCircle';
import { useAgentProfile } from '@/hooks/useAgentProfile';
import { Link } from 'react-router-dom';
import { BookingCTA } from './BookingCTA';
import { useBookingModal } from '@/hooks/useBookingModal';
import { ChatProductCard } from './ChatProductCard';
import { ChatComparisonGrid } from './ChatComparisonGrid';
import { QuotaUpsellModal } from './QuotaUpsellModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface ChatInterfaceProps {
  initialMessage?: string;
  isNewChat?: boolean;
  conversationId?: string | null;
}

// Helper function to clean AI formatting tags and JSON leakage from message content
const cleanMessageContent = (content: string): string => {
  return content
    .replace(/<agent_to_user_start>/g, '')
    .replace(/<agent_to_user_end>/g, '')
    .replace(/<\/agent_to_user_start>/g, '')
    .replace(/<\/agent_to_user_end>/g, '')
    // Remove any JSON objects that leaked through
    .replace(/\{[^}]*"[^}]*\}/g, '')
    .replace(/\s{2,}/g, ' ') // Clean up extra whitespace
    .trim();
};

export function ChatInterface({ initialMessage, isNewChat = false, conversationId: propConversationId }: ChatInterfaceProps = {}) {
  const { user } = useAuth();
  const { profile } = useAgentProfile();
  const { markConversationStarted, setCurrentConversationId, conversationTitle } = useChat();
  const { sendMessage, isStreaming, error: streamError } = useChatStream();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(propConversationId || null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasProcessedInitialMessage, setHasProcessedInitialMessage] = useState(false);
  const [isAutoSending, setIsAutoSending] = useState(false);
  const [showBookingSuggestion, setShowBookingSuggestion] = useState(false);
  const [quotaError, setQuotaError] = useState<any>(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { openBooking, BookingModal, PreloadIframe } = useBookingModal("Circle Expert", "https://link.circlehub.org/widget/booking/XMerHP4ILIgxbU9odtIS");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize conversation or load messages for specific conversation
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const init = async () => {
      try {
        let convId: string;

        // If conversation ID provided (switching to existing conversation)
        if (propConversationId) {
          convId = propConversationId;
        } else if (isNewChat) {
          // Create a brand new conversation explicitly with title if provided
          console.log('[ChatInterface] Creating new conversation...');
          convId = await createNewConversation(user.id, profile?.id, conversationTitle || undefined);
          console.log('[ChatInterface] New conversation created:', convId);
        } else {
          // Get or create conversation (for backward compatibility)
          convId = await getOrCreateConversation(user.id, profile?.id);
        }
        
        setConversationId(convId);
        setCurrentConversationId(convId);

        // Only load history if this is NOT a new chat
        if (!isNewChat) {
          const history = await getConversationMessages(convId);
          setMessages(
            history.map(m => ({
              role: m.role as any,
              content: m.content,
              metadata: m.metadata,
            }))
          );
        } else {
          // Clear messages for new chat, but DON'T clear if auto-send is in progress
          if (!isAutoSending) {
            setMessages([]);
          }
        }
      } catch (err) {
        console.error('Failed to init conversation:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user?.id, profile?.id, isNewChat, propConversationId, conversationTitle, setCurrentConversationId, isAutoSending]);

  // Handle initial message - auto-submit
  useEffect(() => {
    if (initialMessage && conversationId && !hasProcessedInitialMessage && !isLoading) {
      console.log('[ChatInterface] Auto-sending initial message:', initialMessage);
      setHasProcessedInitialMessage(true);
      setIsAutoSending(true);
      
      // Create and send the user message directly
      const autoSend = async () => {
        try {
          const userMessage: Message = {
            role: 'user',
            content: initialMessage.trim(),
          };

          setMessages(prev => [...prev, userMessage]);
          
          // Save user message
          await saveMessage(conversationId, 'user', userMessage.content);

          // Prepare assistant message
          let assistantContent = '';
          let currentMetadata: any = {};
          
          const updateAssistant = (chunk: string, metadata?: any) => {
            assistantContent += chunk;
            if (metadata) currentMetadata = metadata;
            
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return prev.map((m, i) =>
                  i === prev.length - 1 ? { ...m, content: assistantContent, metadata: currentMetadata } : m
                );
              }
              return [...prev, { role: 'assistant', content: assistantContent, metadata: currentMetadata }];
            });
          };

          await sendMessage(
            conversationId,
            [userMessage],
            updateAssistant,
            async () => {
              if (currentMetadata?.suggest_booking) {
                setShowBookingSuggestion(true);
              }
              await saveMessage(conversationId, 'assistant', assistantContent, currentMetadata);
            }
          );
        } catch (err: any) {
          console.error('Auto-send error:', err);
          if (err.quotaError) {
            setQuotaError(err.quotaError);
            setShowQuotaModal(true);
          }
        } finally {
          setIsAutoSending(false);
        }
      };

      autoSend();
    }
  }, [initialMessage, conversationId, hasProcessedInitialMessage, isLoading, sendMessage]);

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;
    
    // If no user, we need to prompt login
    if (!user?.id || !conversationId) {
      console.log('[ChatInterface] No user or conversation, cannot send message');
      return;
    }

    // Mark that user has started a conversation (for persistent chat button)
    markConversationStarted();

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Save user message
    await saveMessage(conversationId, 'user', userMessage.content);

    // Prepare assistant message placeholder
    let assistantContent = '';
    let currentMetadata: any = {};
    
    const updateAssistant = (chunk: string, metadata?: any) => {
      assistantContent += chunk;
      if (metadata) currentMetadata = metadata;
      
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantContent, metadata: currentMetadata } : m
          );
        }
        return [...prev, { role: 'assistant', content: assistantContent, metadata: currentMetadata }];
      });
    };

    try {
      await sendMessage(
        conversationId,
        [...messages, userMessage],
        updateAssistant,
        async () => {
          // Check if AI suggested booking
          if (currentMetadata?.suggest_booking) {
            setShowBookingSuggestion(true);
          }
          
          // Save assistant message
              await saveMessage(conversationId, 'assistant', assistantContent, currentMetadata);
              
              // Auto-generate title from first user message
              if (messages.length === 0) {
                const title = userMessage.content.length > 40
                  ? userMessage.content.substring(0, 40) + '...'
                  : userMessage.content;
                await updateConversationTitle(conversationId, title);
              }
            }
      );
    } catch (err: any) {
      console.error('Send message error:', err);
      if (err.quotaError) {
        setQuotaError(err.quotaError);
        setShowQuotaModal(true);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <div className="text-muted-foreground">
          <TypingIndicator />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-w-0">
        {messages.length === 0 && !isAutoSending && (
          <Card className="p-8 text-center bg-muted/50">
            <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h3 className="text-base font-semibold mb-2">
              Hey, let's find you the right tool.
            </h3>
            <p className="text-sm text-muted-foreground">
              What's eating up your time right now? Let's find you the right tool — no sales pitch, just straight talk.
            </p>
          </Card>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className="space-y-3">
            <div
              className={`flex ${
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <Card
                className={`max-w-[80%] px-3 py-2 ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}
              >
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{cleanMessageContent(msg.content)}</p>

                {msg.metadata?.productSlugs && (
                  <div className="mt-3 space-y-2">
                    {msg.metadata.productSlugs.map((slug: string) => (
                      <Link
                        key={slug}
                        to={`/product/${slug}`}
                        className="block"
                      >
                        <Badge
                          variant="secondary"
                          className="cursor-pointer hover:bg-secondary/80"
                        >
                          View Product →
                        </Badge>
                      </Link>
                    ))}
                  </div>
                )}
              </Card>
            </div>
            
            {/* Product Cards */}
            {msg.metadata?.products && msg.metadata?.displayType === 'card' && (
              <div className="flex justify-start">
                <div className="max-w-[80%] space-y-2">
                  {msg.metadata.products.map((product: any, i: number) => (
                    <ChatProductCard key={i} product={product} />
                  ))}
                </div>
              </div>
            )}

            {/* Comparison Grid */}
            {msg.metadata?.products && msg.metadata?.displayType === 'comparison' && (
              <div className="flex justify-start">
                <div className="w-full">
                  <ChatComparisonGrid 
                    products={msg.metadata.products}
                    winner={msg.metadata.winner}
                    winnerReason={msg.metadata.winnerReason}
                  />
                </div>
              </div>
            )}
            
            {msg.metadata?.suggest_booking && showBookingSuggestion && (
              <div className="flex justify-start">
                <div className="max-w-[80%]">
                  <BookingCTA 
                    conversationId={conversationId || undefined} 
                    onBookingClick={() => openBooking()}
                    isAutoTriggered={msg.metadata?.suggest_booking?.auto_triggered}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
          <div className="flex justify-start">
            <Card className="max-w-[80%] px-3 py-2 bg-muted">
              <TypingIndicator size="small" />
            </Card>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {streamError && !quotaError && (
        <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm">
          {streamError}
        </div>
      )}

      {quotaError && (
        <div className="px-4 py-2">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {quotaError.message || 'Daily message limit reached'}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="px-4 py-2 border-t bg-background space-y-2">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              quotaError 
                ? "Daily limit reached..." 
                : !user?.id 
                  ? "Sign in to chat..." 
                  : "What tool are you looking for?"
            }
            disabled={isStreaming || !!quotaError || !user?.id}
            className="flex-1 border-2 border-border/80 focus-visible:border-border"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isStreaming || !!quotaError || !user?.id || !conversationId}
            size="icon"
          >
            {isStreaming ? (
              <TypingIndicator size="small" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        {/* Persistent booking button */}
        <div className="flex justify-center">
          <Button
            onClick={() => openBooking()}
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground px-6 py-1.5 h-8 transition-colors"
          >
            <Phone className="mr-1.5 h-3 w-3" />
            Book Human Advisor
          </Button>
        </div>
      </div>
      <PreloadIframe />
      <BookingModal />
      <QuotaUpsellModal 
        open={showQuotaModal} 
        onClose={() => setShowQuotaModal(false)}
        quotaData={quotaError}
      />
    </div>
  );
}
